import './dev.js';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';

import 'bootstrap/dist/css/bootstrap.min.css'; // Bootstrap CSS
import 'bootstrap/dist/js/bootstrap.bundle.min'; // Bootstrap Bundle JS
import 'bootstrap-icons/font/bootstrap-icons.css';
import './index.scss';

import { I18nextProvider } from 'react-i18next';
import i18n from './utils/i18n'; // the initialized i18next instance

import './utils/logger'; // needed for the logger to be initialez
import store from './store';
import { fetchConfig, loadConfig, setWebsiteStyles } from './store/slices/templatesConfig';
import { fetchSources } from './store/slices/dataSources';

import App from './app';
import ErrorBoundary from './components/error-boundary';

import { installAppAxiosCache } from './utils/install-axios-cache';
import { installAuthInterceptor } from './utils/auth-interceptor';
import { loadPulsar } from './utils/analytics/pulsar';
import axios from 'axios';

installAppAxiosCache(axios, window.config as unknown as Record<string, unknown>);

// Stale-session guard: any non-auth API call that 401/403s tears down the
// session and redirects to /register (see auth-interceptor for the rationale).
installAuthInterceptor(axios);

// Pulsar first-party analytics beacon (heimdall). Fired at boot, independent of the
// marketing-consent gate that governs GA/FB/Reddit — see src/utils/analytics/pulsar.ts.
loadPulsar();

// add the root, page-layout and page style rules containers (as style tags)
ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <ErrorBoundary>
    <Provider store={store}>
      <I18nextProvider i18n={i18n}>
        <App />
      </I18nextProvider>
    </Provider>
  </ErrorBoundary>,
);

// Tell the boot watchdog (index.html) the module graph loaded and React started:
// reaching this line means every static import resolved. It cancels the watchdog
// and clears its one-shot reload guard. A crash DURING render is the
// ErrorBoundary's job, not the watchdog's.
(window as any).__appBooted?.();

if (window.config.preloadDS) {
  try {
    const values: any = Object.values(window.config.preloadDS);
    if (Array.isArray(values) && values.length > 0) {
      store.dispatch(fetchSources({ ids: values }));
    }
  } catch (e) {
    /* do nothing */
  }
}

// get the pages configuration; TODO: fix the hardcoded tenantID and siteId
if (import.meta.env.DEV) {
  const tenantId = window.config.clientId;
  const siteId = window.config.siteId;
  document.documentElement.classList.toggle(`-s-${siteId}`);
  store.dispatch(fetchConfig({ tenantId: tenantId, siteId: siteId }));
} else {
  document.documentElement.classList.toggle(`-s-${window.config.siteId}`);
  if (window.config.preloadTmplConfig === '1') {
    if (typeof window.tmplConfigPromise === 'object' && typeof window.tmplConfigPromise.then === 'function') {
      window.tmplConfigPromise
        .then((config: any) => {
          setWebsiteStyles({
            setCSSVariables: config['set_css_variables'],
            setCSSLightVariables: config['set_css_light_variables'],
            setCSSDarkVariables: config['set_css_dark_variables'],
            fonts: config['fonts'],
            breakPoints: config['break_points'],
            commonCSS: config['common_css'],
            themeMode: config['theme_mode'],
          });

          setTimeout(() => {
            console.time('fetchConfig.query');
            store.dispatch(loadConfig({ data: config }));
          }, 10);
        })
        .catch((err: any) => {
          store.dispatch(fetchConfig({ tenantId: window.config.clientId, siteId: window.config.siteId }));
        });
    }
  } else {
    store.dispatch(fetchConfig({ tenantId: window.config.clientId, siteId: window.config.siteId }));
  }
}

store.dispatch({ type: 'APPLICATION_INITIALIZE' });

// for inspecting state in console dev
// @ts-ignore
window._store = store;

const showErrorOverlay = (err: any) => {
  console.error('error', err);
};

import.meta.hot?.on('vite:error', showErrorOverlay);
